import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge } from 'react-bootstrap'
import { api } from '../../services/api'

interface Category {
  id: string
  name: string
  description?: string
  color: string
  isActive: boolean
  createdAt: string
  _count: {
    tickets: number
  }
}

interface CategoryData {
  name: string
  description: string
  color: string
}

export const CategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryData, setCategoryData] = useState<CategoryData>({
    name: '',
    description: '',
    color: '#007bff'
  })
  const [submitting, setSubmitting] = useState(false)

  const colorOptions = [
    { value: '#007bff', label: 'Blue' },
    { value: '#28a745', label: 'Green' },
    { value: '#ffc107', label: 'Yellow' },
    { value: '#dc3545', label: 'Red' },
    { value: '#6f42c1', label: 'Purple' },
    { value: '#fd7e14', label: 'Orange' },
    { value: '#20c997', label: 'Teal' },
    { value: '#6c757d', label: 'Gray' }
  ]

  useEffect(() => {
    fetchCategories()
  }, [])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/categories')
      setCategories(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      if (editingCategory) {
        await api.patch(`/admin/categories/${editingCategory.id}`, categoryData)
      } else {
        await api.post('/admin/categories', categoryData)
      }
      setShowModal(false)
      setEditingCategory(null)
      setCategoryData({ name: '', description: '', color: '#007bff' })
      fetchCategories()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save category')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setCategoryData({
      name: category.name,
      description: category.description || '',
      color: category.color
    })
    setShowModal(true)
  }

  const handleDelete = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await api.delete(`/admin/categories/${categoryId}`)
        fetchCategories()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete category')
      }
    }
  }

  const handleToggleStatus = async (categoryId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/categories/${categoryId}`, { isActive: !isActive })
      fetchCategories()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update category')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="categories-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Categories Management</h2>
          <p className="text-muted mb-0 small">Organize tickets by categories</p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)} className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          Add Category
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{categories.length}</h3>
              <p className="text-muted mb-0">Total Categories</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{categories.filter(c => c.isActive).length}</h3>
              <p className="text-muted mb-0">Active Categories</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{categories.reduce((sum, c) => sum + c._count.tickets, 0)}</h3>
              <p className="text-muted mb-0">Total Tickets</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">
                {categories.length > 0 ? Math.round(categories.reduce((sum, c) => sum + c._count.tickets, 0) / categories.length) : 0}
              </h3>
              <p className="text-muted mb-0">Avg Tickets/Category</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Categories Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-tags me-2 text-primary"></i>
            Categories ({categories.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {categories.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Color</th>
                  <th>Tickets</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded-circle me-2" 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: category.color 
                          }}
                        ></div>
                        <div className="fw-medium">{category.name}</div>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted">
                        {category.description || 'No description'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded me-2" 
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            backgroundColor: category.color 
                          }}
                        ></div>
                        <span className="text-muted">{category.color}</span>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">{category._count.tickets}</Badge>
                    </td>
                    <td>
                      <Badge bg={category.isActive ? 'success' : 'danger'}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(category.createdAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEdit(category)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant={category.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleStatus(category.id, category.isActive)}
                        >
                          <i className={`fas fa-${category.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(category.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-tags text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No categories found</h5>
              <p className="text-muted">Create your first category using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Category Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false)
        setEditingCategory(null)
        setCategoryData({ name: '', description: '', color: '#007bff' })
      }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                value={categoryData.name}
                onChange={(e) => setCategoryData(prev => ({ ...prev, name: e.target.value }))}
                required
                placeholder="e.g., Technical Support"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={categoryData.description}
                onChange={(e) => setCategoryData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this category is for..."
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`btn ${categoryData.color === color.value ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
                    onClick={() => setCategoryData(prev => ({ ...prev, color: color.value }))}
                    style={{ minWidth: '60px' }}
                  >
                    <div 
                      className="rounded-circle d-inline-block me-2" 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        backgroundColor: color.value 
                      }}
                    ></div>
                    {color.label}
                  </button>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false)
            setEditingCategory(null)
            setCategoryData({ name: '', description: '', color: '#007bff' })
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || !categoryData.name.trim()}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingCategory ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editingCategory ? 'Update Category' : 'Create Category'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

